import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminApi } from "@/lib/api";
import { Product, Category } from "@/types/admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash2, Package, Edit, RefreshCw, Search, X, ChevronLeft, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ImageUploader } from "./ImageUploader";
import { supabase } from "@/lib/supabase";

export function ProductsTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Estados
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>("all");
  const [showingNewProductForm, setShowingNewProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [newSpecLabel, setNewSpecLabel] = useState("");
  const [newSpecValue, setNewSpecValue] = useState("");
  const [isVideoUploading, setIsVideoUploading] = useState(false);

  // Estados para el modal de confirmación de eliminación
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState<number | null>(null);
  const [productNameToDelete, setProductNameToDelete] = useState<string>("");

  // Estado inicial para newProduct incluyendo videoUrl
  const initialNewProductState: Partial<Product> & { videoUrl?: string | null } = {
    name: "",
    description: "",
    price: "",
    brand: "",
    images: [],
    categoryId: null,
    condition: "new",
    inStock: true,
    features: [],
    technicalSpecs: [],
    videoUrl: null,
  };

  const [newProduct, setNewProduct] = useState<Partial<Product> & { videoUrl?: string | null }>(initialNewProductState);

  // Queries
  const {
    data: products = [],
    isLoading: isLoadingProducts
  } = useQuery<Product[]>({
    queryKey: ["products"],
    queryFn: adminApi.products.getAll
  });

  const {
    data: categories = [],
    isLoading: isLoadingCategories
  } = useQuery<Category[]>({
    queryKey: ["categories"],
    queryFn: adminApi.categories.getAll
  });

  // Mutations
  const deleteProductMutation = useMutation({
    mutationFn: adminApi.products.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast({
        title: "Producto eliminado",
        description: "El producto ha sido eliminado correctamente.",
      });
      // Limpiar estados del modal
      setShowDeleteModal(false);
      setProductToDelete(null);
      setProductNameToDelete("");
    },
    onError: (error) => {
      toast({
        title: "Error al eliminar el producto",
        description: error.message,
        variant: "destructive",
      });
      // Limpiar estados del modal incluso en caso de error
      setShowDeleteModal(false);
      setProductToDelete(null);
      setProductNameToDelete("");
    }
  });

  const saveProductMutation = useMutation({
    mutationFn: (product: Partial<Product>) => {
      // Asegurarnos de que los arrays estén inicializados correctamente y que categoryId sea un número
      const productToSave = {
        ...product,
        images: product.images || [],
        features: product.features || [],
        technicalSpecs: product.technicalSpecs || [],
        categoryId: product.categoryId ? Number(product.categoryId) : null,
        videoUrl: product.videoUrl || null // Asegurar que videoUrl siempre tenga un valor, aunque sea null
      };

      console.log("Guardando producto con datos:", productToSave);
      console.log("CategoryId tipo:", typeof productToSave.categoryId, "valor:", productToSave.categoryId);
      console.log("TechnicalSpecs:", productToSave.technicalSpecs);
      console.log("VideoUrl:", productToSave.videoUrl, "tipo:", typeof productToSave.videoUrl);

      if (editingProduct) {
        return adminApi.products.update(editingProduct.id, productToSave);
      } else {
        return adminApi.products.create(productToSave as any);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      setEditingProduct(null);
      setNewProduct(initialNewProductState);
      toast({
        title: editingProduct ? "Producto actualizado" : "Producto creado",
        description: editingProduct
          ? "El producto ha sido actualizado correctamente."
          : "El producto ha sido creado correctamente.",
      });
      setShowingNewProductForm(false);
    },
    onError: (error) => {
      toast({
        title: "Error al guardar el producto",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Handlers
  const handleEditProduct = (product: Product) => {
    console.log("Editando producto:", product);
    const productData = {
      ...product,
      categoryId: product.categoryId ? Number(product.categoryId) : null,
      images: product.images || [],
      features: product.features || [],
      technicalSpecs: product.technicalSpecs || [],
      videoUrl: product.videoUrl || null // Asegurar que sea explícitamente null en lugar de undefined
    };
    console.log("Datos de producto preparados para edición (con videoUrl):", productData);
    setEditingProduct(product);
    setNewProduct(productData);
    setShowingNewProductForm(true);
  };

  const handleDeleteProduct = (productId: number, productName: string) => {
    // En lugar de usar confirm, mostramos nuestro modal personalizado
    setProductToDelete(productId);
    setProductNameToDelete(productName);
    setShowDeleteModal(true);
  };

  // Función para confirmar la eliminación desde el modal
  const confirmDeleteProduct = () => {
    if (productToDelete !== null) {
      deleteProductMutation.mutate(productToDelete);
    }
  };

  // Función para cancelar la eliminación
  const cancelDeleteProduct = () => {
    setShowDeleteModal(false);
    setProductToDelete(null);
    setProductNameToDelete("");
  };

  // Función para validar el precio
  const validatePrice = (price: string | undefined): boolean => {
    if (!price || !price.trim()) {
      return false;
    }

    // Si es un precio numérico, debe tener el símbolo de euro
    if (/^\d+€?$/.test(price)) {
      return true;
    }

    // Si es un texto descriptivo (como "Consultar precio"), también es válido
    if (price.trim().length > 3) {
      return true;
    }

    return false;
  };

  const handleSaveProduct = async () => {
    if (!newProduct.name?.trim()) {
      toast({
        title: "Error",
        description: "El nombre del producto es requerido",
        variant: "destructive",
      });
      return;
    }

    if (!validatePrice(newProduct.price)) {
      toast({
        title: "Error",
        description: "El precio del producto es requerido y debe tener un formato válido",
        variant: "destructive",
      });
      return;
    }

    try {
      // Asegurarse de que technicalSpecs y videoUrl estén en el objeto a guardar
      // Es importante que videoUrl siempre sea null si no hay valor, NUNCA undefined
      const productToSave = {
        ...newProduct,
        technicalSpecs: newProduct.technicalSpecs || [],
        videoUrl: newProduct.videoUrl || null
      };

      console.log("Guardando producto (con videoUrl):", productToSave);
      console.log("URL del video a guardar:", productToSave.videoUrl);

      await saveProductMutation.mutateAsync(productToSave);
    } catch (error) {
      console.error("Error al guardar el producto:", error);
    }
  };

  // Handlers para arrays
  const handleAddFeature = () => {
    if (newSpecLabel.trim() && newSpecValue.trim()) {
      // Asegurarse de que technicalSpecs esté inicializado
      const currentSpecs = newProduct.technicalSpecs || [];

      setNewProduct({
        ...newProduct,
        technicalSpecs: [
          ...currentSpecs,
          { label: newSpecLabel.trim(), value: newSpecValue.trim() }
        ]
      });

      console.log("Especificación técnica añadida:", { label: newSpecLabel.trim(), value: newSpecValue.trim() });
      console.log("Especificaciones técnicas actualizadas:", [
        ...currentSpecs,
        { label: newSpecLabel.trim(), value: newSpecValue.trim() }
      ]);

      setNewSpecLabel("");
      setNewSpecValue("");
    }
  };

  // Función para formatear el precio
  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Si el valor es numérico, formateamos automáticamente
    if (/^\d+$/.test(value)) {
      // Convertir a número y formatear con el símbolo de euro
      const formattedPrice = `${value}€`;
      setNewProduct({...newProduct, price: formattedPrice});
    } else {
      // Si no es numérico (por ejemplo, "Consultar precio"), lo dejamos tal cual
      setNewProduct({...newProduct, price: value});
    }
  };

  const handleRemoveFeature = (index: number) => {
    const updatedFeatures = [...(newProduct.features || [])];
    updatedFeatures.splice(index, 1);
    setNewProduct({...newProduct, features: updatedFeatures});
  };

  const handleAddSpec = () => {
    if (newSpecLabel.trim() && newSpecValue.trim()) {
      // Asegurarse de que technicalSpecs esté inicializado
      const currentSpecs = newProduct.technicalSpecs || [];

      setNewProduct({
        ...newProduct,
        technicalSpecs: [
          ...currentSpecs,
          { label: newSpecLabel.trim(), value: newSpecValue.trim() }
        ]
      });

      console.log("Especificación técnica añadida:", { label: newSpecLabel.trim(), value: newSpecValue.trim() });
      console.log("Especificaciones técnicas actualizadas:", [
        ...currentSpecs,
        { label: newSpecLabel.trim(), value: newSpecValue.trim() }
      ]);

      setNewSpecLabel("");
      setNewSpecValue("");
    }
  };

  const handleRemoveSpec = (index: number) => {
    const updatedSpecs = [...(newProduct.technicalSpecs || [])];
    updatedSpecs.splice(index, 1);
    setNewProduct({...newProduct, technicalSpecs: updatedSpecs});
  };

  // Filtrado de productos
  const filteredProducts = products.filter(product => {
    // Filtrado por categoría
    if (selectedCategoryFilter !== "all") {
      const selectedCategory = categories.find(cat => cat.id === parseInt(selectedCategoryFilter));
      if (selectedCategory) {
        // Si la categoría seleccionada es de nivel 1 (principal)
        if (selectedCategory.level === 1) {
          // Obtener todas las subcategorías que pertenecen a esta categoría principal
          const subcategoryIds = categories
            .filter(cat => cat.path.startsWith(selectedCategory.path))
            .map(cat => cat.id);

          // Incluir productos de la categoría principal y sus subcategorías
          if (!product.categoryId ||
              (!subcategoryIds.includes(product.categoryId) &&
               product.categoryId !== parseInt(selectedCategoryFilter))) {
            return false;
          }
        } else {
          // Para categorías que no son principales, filtrar normalmente
          if (!product.categoryId || product.categoryId !== parseInt(selectedCategoryFilter)) {
            return false;
          }
        }
      }
    }

    // Filtrado por búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      return (
        product.name.toLowerCase().includes(query) ||
        product.description.toLowerCase().includes(query) ||
        product.brand.toLowerCase().includes(query)
      );
    }

    return true;
  });

  // Resetear newProduct incluyendo videoUrl
  const resetNewProduct = () => {
    setNewProduct(initialNewProductState);
  };

  // Handler para la subida de video (Modificado para usar adminApi)
  const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validaciones (tipo, tamaño)
    if (!file.type.startsWith('video/')) {
      toast({ title: "Tipo inválido", description: "Selecciona un archivo de video.", variant: "destructive" });
      return;
    }
    if (file.size > 50 * 1024 * 1024) { // Límite de 50MB
      toast({ title: "Archivo grande", description: "Máximo 50MB para videos.", variant: "destructive" });
      return;
    }

    setIsVideoUploading(true);
    toast({ title: "Subiendo video..." });

    try {
      // Llamar a la nueva función de la API del backend
      const videoUrl = await adminApi.products.uploadVideo(file);

      if (!videoUrl) {
        throw new Error("La API no devolvió una URL para el video.");
      }

      // Actualizar estado de newProduct con la URL devuelta por el backend
      setNewProduct(prev => ({ ...prev, videoUrl: videoUrl }));

      toast({
        title: "Video subido",
        description: "Video subido y URL guardada exitosamente.",
      });

    } catch (error: any) {
      console.error("Error en handleVideoUpload llamando a adminApi:", error);
      toast({
        title: "Error al subir el video",
        description: error.message || "Ocurrió un error inesperado al contactar la API.",
        variant: "destructive",
      });
      // Opcional: Limpiar videoUrl si falla la subida
      // setNewProduct(prev => ({ ...prev, videoUrl: "" }));
    } finally {
      setIsVideoUploading(false);
       // Limpiar el input de archivo
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  if (isLoadingProducts || isLoadingCategories) {
    return <div>Cargando...</div>;
  }

  // Vista del formulario de producto
  if (showingNewProductForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4 mb-6 bg-zinc-900/70 p-4 rounded-xl border border-zinc-800/50 shadow-lg">
          <button
            className="px-4 py-2 text-sm border border-zinc-700 rounded-lg flex items-center gap-2 text-white bg-zinc-800 hover:bg-zinc-700 transition-colors"
            onClick={() => {
              setShowingNewProductForm(false);
              setEditingProduct(null);
              resetNewProduct();
            }}
          >
            <ChevronLeft className="h-4 w-4" /> Volver
          </button>
          <h2 className="text-xl font-semibold text-white flex-1">
            {editingProduct ? `Editar Producto: ${editingProduct.name}` : "Crear Nuevo Producto"}
          </h2>
          <div className="text-xs text-zinc-500 bg-zinc-800/80 py-1 px-3 rounded-full">
            Todos los campos con * son obligatorios
          </div>
        </div>

        <Card className="border-zinc-800/50 bg-zinc-900/70 shadow-xl overflow-hidden">
          <form onSubmit={(e) => { e.preventDefault(); handleSaveProduct(); }} className="divide-y divide-zinc-800/30">
            {/* Sección principal */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Información básica */}
                <div className="space-y-6">
                  <div className="border-l-4 border-[#FCAA0D] pl-4 mb-6">
                    <h3 className="text-lg font-bold text-white mb-1">Información Básica</h3>
                    <p className="text-zinc-400 text-sm">Datos principales del producto</p>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-white flex items-center">
                        Nombre <span className="text-[#FCAA0D] ml-1">*</span>
                      </Label>
                      <Input
                        id="name"
                        value={newProduct.name || ""}
                        onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                        placeholder="Nombre del producto"
                        className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500
                                 focus-visible:ring-[#FCAA0D]/30 focus-visible:border-[#FCAA0D]/50"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description" className="text-white">Descripción <span className="text-[#FCAA0D] ml-1">*</span></Label>
                      <Textarea
                        id="description"
                        value={newProduct.description || ""}
                        onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                        placeholder="Descripción detallada del producto"
                        className="min-h-[120px] bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500
                                 focus-visible:ring-[#FCAA0D]/30 focus-visible:border-[#FCAA0D]/50"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="brand" className="text-white">Marca</Label>
                        <Input
                          id="brand"
                          value={newProduct.brand || ""}
                          onChange={(e) => setNewProduct({...newProduct, brand: e.target.value})}
                          placeholder="Marca del producto"
                          className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500
                                   focus-visible:ring-[#FCAA0D]/30 focus-visible:border-[#FCAA0D]/50"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="price" className="text-white">Precio <span className="text-[#FCAA0D] ml-1">*</span></Label>
                        <div className="relative">
                          <Input
                            id="price"
                            value={newProduct.price || ""}
                            onChange={handlePriceChange}
                            placeholder="Ej. 1200, Consultar precio, etc."
                            className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500
                                     focus-visible:ring-[#FCAA0D]/30 focus-visible:border-[#FCAA0D]/50 pl-8"
                          />
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400">€</span>
                        </div>
                        <p className="text-xs text-zinc-500 mt-1">
                          Formato numérico (1200) o texto (Consultar precio)
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="category" className="text-white">Categoría</Label>
                      <select
                        id="category"
                        value={newProduct.categoryId?.toString() || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          const categoryId = value ? Number(value) : null;
                          setNewProduct({
                            ...newProduct,
                            categoryId: categoryId
                          });
                        }}
                        className="w-full p-2 rounded-md bg-zinc-800 border border-zinc-700 text-white
                                 focus:ring-[#FCAA0D]/30 focus:border-[#FCAA0D]/50"
                      >
                        <option value="">Sin categoría</option>
                        {categories.map(category => (
                          <option key={category.id} value={category.id.toString()}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="condition" className="text-white">Condición</Label>
                        <select
                          id="condition"
                          value={newProduct.condition || "new"}
                          onChange={(e) => setNewProduct({
                            ...newProduct,
                            condition: e.target.value
                          })}
                          className="w-full p-2 rounded-md bg-zinc-800 border border-zinc-700 text-white
                                   focus:ring-[#FCAA0D]/30 focus:border-[#FCAA0D]/50"
                        >
                          <option value="new">Nuevo</option>
                          <option value="used">Usado</option>
                          <option value="refurbished">Reacondicionado</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="in_stock" className="text-white">Disponibilidad</Label>
                        <div className="flex items-center h-10 bg-zinc-800 border border-zinc-700 rounded-md px-3">
                          <input
                            type="checkbox"
                            id="in_stock"
                            checked={newProduct.inStock || false}
                            onChange={(e) => setNewProduct({
                              ...newProduct,
                              inStock: e.target.checked
                            })}
                            className="w-4 h-4 border border-zinc-700 bg-zinc-800 checked:bg-[#FCAA0D] rounded"
                          />
                          <span className="ml-2 text-sm text-white">
                            {newProduct.inStock ? "En stock" : "Sin stock"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Media */}
                <div className="space-y-6">
                  <div className="border-l-4 border-[#FCAA0D] pl-4 mb-6">
                    <h3 className="text-lg font-bold text-white mb-1">Media</h3>
                    <p className="text-zinc-400 text-sm">Imágenes y video del producto</p>
                  </div>

                  <div className="space-y-6">
                    <div className="bg-zinc-800/60 rounded-lg p-4 border border-zinc-700/50">
                      <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-[#FCAA0D]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Imágenes del Producto
                      </h4>
                      <ImageUploader
                        images={newProduct.images || []}
                        onChange={(images) => setNewProduct({...newProduct, images})}
                      />
                    </div>

                    <div className="bg-zinc-800/60 rounded-lg p-4 border border-zinc-700/50">
                      <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-[#FCAA0D]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Video del Producto (Opcional)
                      </h4>
                      <div className="space-y-3">
                        <Input
                          id="videoFile"
                          type="file"
                          accept="video/*"
                          onChange={handleVideoUpload}
                          disabled={isVideoUploading}
                          className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500
                                   file:mr-4 file:py-2 file:px-4
                                   file:rounded-md file:border-0 file:text-sm
                                   file:font-semibold file:bg-[#FCAA0D]/10 file:text-[#FCAA0D]
                                   hover:file:bg-[#FCAA0D]/20
                                   focus-visible:ring-[#FCAA0D]/30 focus-visible:border-[#FCAA0D]/50"
                        />
                        {isVideoUploading && (
                          <div className="flex items-center text-xs text-[#FCAA0D] animate-pulse">
                            <RefreshCw className="mr-2 h-3 w-3 animate-spin" />
                            Subiendo video...
                          </div>
                        )}
                        {newProduct.videoUrl && (
                          <div className="flex items-center justify-between bg-zinc-800/60 rounded p-2 text-xs text-zinc-400 truncate border border-zinc-700/30">
                            <span className="truncate mr-2">Video cargado: {newProduct.videoUrl.split('/').pop()}</span>
                            <button
                              type="button"
                              onClick={() => setNewProduct({...newProduct, videoUrl: null})}
                              className="text-zinc-500 hover:text-red-400 flex-shrink-0"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Características y especificaciones */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="border-l-4 border-[#FCAA0D] pl-4">
                    <h3 className="text-lg font-bold text-white mb-1">Características</h3>
                    <p className="text-zinc-400 text-sm">Añade las características principales</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        value={newSpecLabel}
                        onChange={(e) => setNewSpecLabel(e.target.value)}
                        placeholder="Añadir característica"
                        className="flex-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500
                                 focus-visible:ring-[#FCAA0D]/30 focus-visible:border-[#FCAA0D]/50"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleAddFeature}
                        className="border-zinc-700 text-white hover:bg-[#FCAA0D]/10 hover:text-[#FCAA0D] hover:border-[#FCAA0D]/30"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="bg-zinc-800/40 rounded-lg p-3 min-h-[150px] border border-zinc-700/30">
                      {(newProduct.features || []).length > 0 ? (
                        <ul className="space-y-2">
                          {(newProduct.features || []).map((feature, index) => (
                            <li key={index} className="flex items-center gap-2 bg-zinc-800/90 rounded p-2 pr-1 group">
                              <span className="flex-1 text-sm text-white">{feature}</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveFeature(index)}
                                className="opacity-0 group-hover:opacity-100 h-6 w-6 hover:bg-red-900/20 hover:text-red-400"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="h-full flex items-center justify-center">
                          <div className="text-gray-500 text-sm italic text-center">
                            No hay características definidas
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="border-l-4 border-[#FCAA0D] pl-4">
                    <h3 className="text-lg font-bold text-white mb-1">Especificaciones Técnicas</h3>
                    <p className="text-zinc-400 text-sm">Añade detalles técnicos del producto</p>
                  </div>

                  <div className="space-y-2">
                    <div className="grid grid-cols-5 gap-2">
                      <Input
                        value={newSpecLabel}
                        onChange={(e) => setNewSpecLabel(e.target.value)}
                        placeholder="Nombre"
                        className="col-span-2 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500
                                 focus-visible:ring-[#FCAA0D]/30 focus-visible:border-[#FCAA0D]/50"
                      />
                      <Input
                        value={newSpecValue}
                        onChange={(e) => setNewSpecValue(e.target.value)}
                        placeholder="Valor"
                        className="col-span-2 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500
                               focus-visible:ring-[#FCAA0D]/30 focus-visible:border-[#FCAA0D]/50"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleAddSpec}
                        className="border-zinc-700 text-white hover:bg-[#FCAA0D]/10 hover:text-[#FCAA0D] hover:border-[#FCAA0D]/30"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="bg-zinc-800/40 rounded-lg p-3 min-h-[150px] border border-zinc-700/30">
                      {(newProduct.technicalSpecs || []).length > 0 ? (
                        <div className="space-y-2">
                          {(newProduct.technicalSpecs || []).map((spec, index) => (
                            <div key={index} className="flex items-center gap-2 bg-zinc-800/90 rounded p-2 pr-1 group">
                              <div className="flex-1 grid grid-cols-2 gap-2">
                                <span className="text-sm font-medium text-[#FCAA0D]/90">{spec.label}:</span>
                                <span className="text-sm text-zinc-300">{spec.value}</span>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveSpec(index)}
                                className="opacity-0 group-hover:opacity-100 h-6 w-6 hover:bg-red-900/20 hover:text-red-400"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="h-full flex items-center justify-center">
                          <div className="text-gray-500 text-sm italic text-center">
                            No hay especificaciones técnicas definidas
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Botones de acción */}
            <div className="p-6 bg-zinc-900/90 flex justify-between items-center">
              <div>
                {editingProduct && (
                  <span className="text-xs text-zinc-500">
                    Editando ID: {editingProduct.id} • Creado: {new Date(editingProduct.createdAt).toLocaleDateString()}
                  </span>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowingNewProductForm(false);
                    setEditingProduct(null);
                    resetNewProduct();
                  }}
                  className="border-zinc-700 text-white hover:bg-zinc-800"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saveProductMutation.isPending}
                  className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-semibold min-w-[120px]"
                >
                  {saveProductMutation.isPending && (
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {editingProduct ? "Guardar Cambios" : "Guardar Producto"}
                </Button>
              </div>
            </div>
          </form>
        </Card>
      </div>
    );
  }

  // Vista principal de productos
  return (
    <div className="space-y-6">
      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-zinc-900 p-6 rounded-xl border border-zinc-800 shadow-xl max-w-md w-full animate-in fade-in slide-in-from-bottom-5 duration-300">
            <div className="flex flex-col items-center text-center mb-6">
              <div className="h-12 w-12 rounded-full bg-red-900/30 text-red-400 flex items-center justify-center mb-4">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Confirmar eliminación</h3>
              <p className="text-zinc-400 text-sm">
                ¿Estás seguro de que deseas eliminar este producto? Esta acción no se puede deshacer.
              </p>
              {productNameToDelete && (
                <div className="mt-4 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50 text-white">
                  <span className="font-medium">{productNameToDelete}</span>
                </div>
              )}
            </div>
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                onClick={cancelDeleteProduct}
                className="min-w-[120px] border-zinc-700 text-white hover:bg-zinc-800"
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteProduct}
                className="min-w-[120px] bg-red-600 hover:bg-red-700"
                disabled={deleteProductMutation.isPending}
              >
                {deleteProductMutation.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  "Aceptar"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6 bg-zinc-900/70 p-4 rounded-xl border border-zinc-800/50 shadow-lg">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-white mb-2 font-montserrat flex items-center">
            <Package className="h-5 w-5 mr-2 text-[#FCAA0D]" /> Gestión de productos
          </h2>
          <p className="text-zinc-400 text-sm">
            Administra el catálogo de productos para el marketplace • <span className="text-[#FCAA0D]">{filteredProducts.length}</span> productos encontrados
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              placeholder="Buscar producto..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full sm:w-[220px] bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500
                       focus-visible:ring-[#FCAA0D]/30 focus-visible:border-[#FCAA0D]/50"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 hover:bg-white/5"
                onClick={() => setSearchQuery("")}
              >
                <X className="h-3 w-3 text-zinc-400" />
              </Button>
            )}
          </div>

          <select
            value={selectedCategoryFilter}
            onChange={(e) => setSelectedCategoryFilter(e.target.value)}
            className="px-3 py-2 rounded-md bg-zinc-800 border-zinc-700 text-white w-full sm:w-[200px]
                     focus:ring-[#FCAA0D]/30 focus:border-[#FCAA0D]/50"
          >
            <option value="all">Todas las categorías</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id.toString()}>
                {category.name}
              </option>
            ))}
          </select>

          <Button
            onClick={() => {
              setEditingProduct(null);
              setNewProduct(initialNewProductState);
              setShowingNewProductForm(true);
            }}
            className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-semibold w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Producto
          </Button>
        </div>
      </div>

      {filteredProducts.length === 0 ? (
        <div className="bg-zinc-900/70 rounded-xl border border-zinc-800/50 p-8 text-center shadow-lg">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#FCAA0D]/10 mb-4">
            <Package className="h-8 w-8 text-[#FCAA0D]" />
          </div>
          <h3 className="mt-4 text-xl font-semibold text-white">No hay productos</h3>
          <p className="mt-2 text-sm text-zinc-400 max-w-sm mx-auto">
            {searchQuery
              ? "No se encontraron productos con el término de búsqueda aplicado."
              : "No hay productos registrados. Crea uno nuevo para empezar."}
          </p>
          <Button
            className="mt-6 bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-semibold"
            onClick={() => {
              setEditingProduct(null);
              setNewProduct(initialNewProductState);
              setShowingNewProductForm(true);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Crear Producto
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          <div className="p-4 rounded-xl border border-zinc-800/50 bg-zinc-900/70 shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-zinc-800/30 transition-colors">
                    <TableHead className="text-zinc-400 font-medium">Producto</TableHead>
                    <TableHead className="text-zinc-400 font-medium">Categoría</TableHead>
                    <TableHead className="text-zinc-400 font-medium">Precio</TableHead>
                    <TableHead className="text-zinc-400 font-medium">Marca</TableHead>
                    <TableHead className="text-zinc-400 font-medium">Estado</TableHead>
                    <TableHead className="text-right text-zinc-400 font-medium">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.map((product) => (
                    <TableRow key={product.id} className="border-zinc-800 group hover:bg-zinc-800/30 transition-colors">
                      <TableCell className="text-white">
                        <div className="flex items-center gap-3">
                          <div className="h-14 w-14 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center overflow-hidden shadow-sm">
                            {product.images && product.images.length > 0 ? (
                              <img
                                src={product.images[0]}
                                alt={product.name}
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.src = "https://placehold.co/60?text=Error";
                                }}
                              />
                            ) : (
                              <Package className="h-6 w-6 text-zinc-500" />
                            )}
                          </div>
                          <div className="max-w-[240px]">
                            <div className="font-medium text-white truncate">{product.name}</div>
                            <div className="text-sm text-zinc-400 line-clamp-1">
                              {product.description}
                            </div>
                            {product.videoUrl && (
                              <span className="inline-flex items-center mt-1 text-xs bg-[#FCAA0D]/20 text-[#FCAA0D] rounded-full py-0.5 px-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                Video
                              </span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {product.categoryId ? (
                          <Badge variant="outline" className="bg-zinc-800/60 text-zinc-300 border-zinc-700">
                            {categories.find((cat) => cat.id === product.categoryId)?.name || "Sin categoría"}
                          </Badge>
                        ) : (
                          <span className="text-zinc-500 italic text-sm">Sin categoría</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-[#FCAA0D]">
                          {product.price.includes('€') ? product.price :
                           /^\d+$/.test(product.price) ? `${product.price}€` : product.price}
                        </span>
                      </TableCell>
                      <TableCell className="text-zinc-300">
                        {product.brand || <span className="text-zinc-500 italic text-sm">-</span>}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={product.inStock
                            ? "bg-green-900/20 text-green-400 hover:bg-green-900/30 border border-green-500/30"
                            : "bg-red-900/20 text-red-400 hover:bg-red-900/30 border border-red-500/30"
                          }
                        >
                          {product.inStock ? "En Stock" : "Sin Stock"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditProduct(product)}
                            className="h-8 w-8 bg-zinc-800/80 border border-zinc-700/50 hover:bg-[#FCAA0D]/10 hover:text-[#FCAA0D] hover:border-[#FCAA0D]/30"
                            title="Editar producto"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteProduct(product.id, product.name)}
                            className="h-8 w-8 bg-zinc-800/80 border border-zinc-700/50 hover:bg-red-900/20 hover:text-red-400 hover:border-red-400/30"
                            title="Eliminar producto"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-zinc-900/70 rounded-xl border border-zinc-800/50">
            <div className="text-sm text-zinc-500">
              Mostrando <span className="text-white font-medium">{filteredProducts.length}</span> productos
            </div>
            <div className="flex items-center space-x-1">
              <Button
                variant="outline"
                size="sm"
                className="w-8 h-8 p-0 border-zinc-700 bg-zinc-800/60 text-zinc-400"
                disabled={true}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-8 h-8 p-0 border-zinc-700 bg-[#FCAA0D]/10 text-[#FCAA0D] border-[#FCAA0D]/30"
              >
                1
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-8 h-8 p-0 border-zinc-700 bg-zinc-800/60 text-zinc-400"
                disabled={true}
              >
                <ChevronLeft className="h-4 w-4 rotate-180" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
